import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Loader2, Download, Video } from "lucide-react";
import { toast } from "sonner";
import {
  gerarVideoDeSlides,
  baixarBlob,
  TrilhaId,
  FormatoVideo,
  ModoDuracao,
  SlideDef,
} from "@/components/palpites-show/videoRenderer";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  titulo: string;
  conteudo: string;
}

// Divide o roteiro em cenas/slides. Detecta blocos "CENA N", "[0-3s]" ou linhas em branco.
function roteiroParaSlides(titulo: string, conteudo: string): SlideDef[] {
  const capa: SlideDef = {
    titulo: "IA PALPITE GURU",
    linhas: [titulo],
    destaque: "🎬",
  };

  // Tenta partir por "CENA" ou "[0-Xs]"
  const cenaRegex = /(?=CENA\s+\d+|\[\d+\s*-\s*\d+s?\])/gi;
  let partes = conteudo.split(cenaRegex).map((s) => s.trim()).filter(Boolean);
  if (partes.length < 2) {
    // fallback: divide em parágrafos
    partes = conteudo.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  }

  const slides: SlideDef[] = partes.slice(0, 8).map((bloco, i) => {
    const linhas = bloco
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0 && !/^[━─═\-]{3,}$/.test(l))
      .slice(0, 4)
      .map((l) => (l.length > 60 ? l.slice(0, 57) + "…" : l));
    return {
      titulo: `CENA ${i + 1}`,
      linhas: linhas.length ? linhas : ["…"],
    };
  });

  const cta: SlideDef = {
    titulo: "IA PALPITE GURU",
    linhas: ["Palpites com IA todo dia", "aipalpiteguru.com"],
    destaque: "🚀",
  };

  return [capa, ...slides, cta];
}

const GerarVideoRoteiroModal: React.FC<Props> = ({ open, onOpenChange, titulo, conteudo }) => {
  const [modoDuracao, setModoDuracao] = useState<ModoDuracao>("rapido");
  const [trilha, setTrilha] = useState<TrilhaId>("urbana");
  const [formato, setFormato] = useState<FormatoVideo>("vertical");
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleGerar = async () => {
    setRendering(true);
    setProgress(0);
    try {
      const slides = roteiroParaSlides(titulo, conteudo);
      const blob = await gerarVideoDeSlides(slides, {
        modoDuracao,
        trilha,
        slides: "todos",
        formato,
        onProgress: (p) => setProgress(Math.round(p * 100)),
      });
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      baixarBlob(blob, `roteiro-video-${ts}.webm`);
      toast.success("Vídeo do roteiro gerado!");
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar vídeo.");
    } finally {
      setRendering(false);
      setProgress(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !rendering && onOpenChange(v)}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-500" />
            Gerar Vídeo do Roteiro
          </DialogTitle>
          <DialogDescription>
            Renderiza cada cena do roteiro como um slide. Local, sem créditos de IA.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Duração</Label>
            <Select value={modoDuracao} onValueChange={(v) => setModoDuracao(v as ModoDuracao)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rapido">Rápido (capa 10s + cenas 20s)</SelectItem>
                <SelectItem value="padrao">Padrão (capa 20s + cenas 40s)</SelectItem>
                <SelectItem value="curto30">Curto total 30s</SelectItem>
                <SelectItem value="curto15">Curto total 15s</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Formato</Label>
            <Select value={formato} onValueChange={(v) => setFormato(v as FormatoVideo)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">Vertical 1080×1920</SelectItem>
                <SelectItem value="horizontal">Horizontal 1920×1080</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Trilha</Label>
            <Select value={trilha} onValueChange={(v) => setTrilha(v as TrilhaId)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="urbana">Energia Urbana</SelectItem>
                <SelectItem value="suspense">Suspense Dourado</SelectItem>
                <SelectItem value="vitoria">Vitória</SelectItem>
                <SelectItem value="nenhuma">Sem trilha</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rendering && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-xs text-center text-muted-foreground">
                Gravando... {progress}%
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={rendering}>
            Cancelar
          </Button>
          <Button
            onClick={handleGerar}
            disabled={rendering}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold"
          >
            {rendering ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Gerar e Baixar</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GerarVideoRoteiroModal;
